import { useRef, useCallback, useEffect, useState, forwardRef } from 'react'
import { Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import { useUserSearch } from '@/hooks/useUserSearch'
import type { UserRead } from '@/api/shares'

interface UserSearchPickerProps {
  query: string
  onQueryChange: (query: string) => void
  selectedUser: UserRead | null
  onSelectUser: (user: UserRead | null) => void
  /** Users hidden from results (e.g. owner and people who already have access) */
  excludeUserIds?: Set<string>
}

/**
 * Search-as-you-type user picker with infinite scroll, shared by the
 * plan share dialogs.
 */
export const UserSearchPicker = forwardRef<
  HTMLInputElement,
  UserSearchPickerProps
>(function UserSearchPicker(
  { query, onQueryChange, selectedUser, onSelectUser, excludeUserIds },
  ref
) {
  const [showDropdown, setShowDropdown] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const {
    data: searchData,
    isLoading: isSearching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useUserSearch({ query })

  const users = (searchData?.pages.flatMap(page => page.users) ?? []).filter(
    u => !excludeUserIds?.has(u.id)
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasNextPage || isFetchingNextPage) return

    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleSelect = (user: UserRead) => {
    onSelectUser(user)
    onQueryChange(user.email || user.display_name || '')
    setShowDropdown(false)
  }

  // Show dropdown only when query >= 2 chars
  const shouldShowDropdown = showDropdown && query.length >= 2

  return (
    <div className="relative flex-1" ref={containerRef}>
      <Input
        ref={ref}
        placeholder="Search by email or name..."
        value={query}
        onChange={e => {
          onQueryChange(e.target.value)
          onSelectUser(null)
          setShowDropdown(true)
        }}
        onFocus={() => {
          if (query.length >= 2) setShowDropdown(true)
        }}
        className="w-full"
      />

      {shouldShowDropdown && (
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto z-50"
        >
          {isSearching && !isFetchingNextPage ? (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : users.length > 0 ? (
            <ul className="py-1">
              {users.map(user => {
                const colors = getAvatarColor(user.id)
                return (
                  <li key={user.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(user)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2',
                        'hover:bg-muted cursor-pointer',
                        'text-left transition-colors duration-150',
                        selectedUser?.id === user.id && 'bg-muted'
                      )}
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarFallback
                          className={cn(
                            colors.bg,
                            colors.text,
                            'text-xs font-medium'
                          )}
                        >
                          {getInitials(user.display_name, user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {user.display_name || user.email}
                        </p>
                        {user.display_name && user.email && (
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
              {isFetchingNextPage && (
                <li className="px-4 py-3 text-center">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                </li>
              )}
            </ul>
          ) : (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <span className="text-sm">
                No users found for &ldquo;{query}&rdquo;
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
})
